import { Formik } from 'formik'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  ContractorPortalUpdatePhone,
  ContractorPortalUpdatePhoneSendCode,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ContractorPortalUpdatePhoneEnterCodeModalValues } from './ContractorPortalUpdatePhoneEnterCodeModal.interface'
import { ContractorPortalUpdatePhoneEnterCodeModalSchema } from './ContractorPortalUpdatePhoneEnterCodeModal.validator'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal, Typography } from '@mui/material'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { ResendCode } from 'src/components/shared/ResendCode'
import { SignInPhoneResponse } from 'src/interfaces/auth'
import { OtpInputFields } from 'src/components/auth/otp-input-form/OtpInputFields'

interface Props {
  token: string
  phone: string
  onClose: () => void
  onSuccess: () => void
}

const initialValues: ContractorPortalUpdatePhoneEnterCodeModalValues = {
  code: '',
}

export const ContractorPortalUpdatePhoneEnterCodeModal = ({
  token,
  phone,
  onSuccess,
  onClose,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const container = useMemo(() => {
    return document.getElementById('root')
  }, [])

  const handleFormSubmit = async (values: ContractorPortalUpdatePhoneEnterCodeModalValues) => {
    setIsLoading(true)
    setError(null)

    try {
      await createAsyncAction(
        dispatch,
        ContractorPortalUpdatePhone.request({ ...values, phone, token }),
      )

      onSuccess()
    } catch (submitError: any) {
      setError(submitError)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    try {
      const response: SignInPhoneResponse = await createAsyncAction(
        dispatch,
        ContractorPortalUpdatePhoneSendCode.request({ phone, token, resend: true }),
      )

      return response
    } catch (resendError: any) {}
  }

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
      disableScrollLock
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ContractorPortalUpdatePhoneEnterCodeModalSchema}
      >
        {({ handleSubmit }) => {
          return (
            <div css={tw`max-w-[590px] mx-4 flex-col p-4 pt-6 bg-shades-0 rounded-2xl`}>
              <Button
                css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
                color="light"
                size="medium"
                leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
                onClick={onClose}
                data-testid="contractors-list-update-phone-enter-code-cancel-btn"
              >
                Cancel
              </Button>
              <Typography css={tw`mt-6`} variant="h2">
                Verify your phone number
              </Typography>
              <Typography css={tw`text-shades-400 mt-2 mb-6 w-[326px]`}>
                {`We sent a code to your new number ending in ${phone.slice(
                  -4,
                )}. Enter it below to confirm and save
              your new phone number.`}
              </Typography>
              <OtpInputFields testIdPrefix="contractors-list-update-phone-enter-code-input" />
              <FormErrorMessage css={tw`mb-6`} error={error} />
              <Button
                className={tw`w-full mb-6`}
                loading={isLoading}
                onClick={() => {
                  handleSubmit()
                }}
                data-testid="contractors-list-update-phone-enter-code-continue-btn"
              >
                Continue
              </Button>
              <ResendCode
                platform="contractorPortal"
                onResend={handleResend}
                resendLinkTestId="contractors-list-update-phone-enter-code-resend-link"
              />
            </div>
          )
        }}
      </Formik>
    </Modal>
  )
}
